import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { extractErrorMessage } from '../../utils/errorHelpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULTS = ['name', 'brand', 'price_detail', 'image_url'];

export const ProductExportModal = ({ open, onClose, filteredIds = [], totalCount = 0 }) => {
  const [columns, setColumns] = useState([]);
  const [chosen, setChosen] = useState(new Set(DEFAULTS));
  const [format, setFormat] = useState('csv');
  const [onlyFiltered, setOnlyFiltered] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/api/products/export/columns`)
      .then(r => setColumns(r.data))
      .catch(() => toast.error('Impossible de charger les colonnes'));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const toggle = (key) => setChosen(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const groups = columns.reduce((acc, c) => {
    (acc[c.group] = acc[c.group] || []).push(c);
    return acc;
  }, {});

  const canFilter = filteredIds.length > 0 && filteredIds.length < totalCount;
  const count = onlyFiltered && canFilter ? filteredIds.length : totalCount;

  const doExport = async () => {
    const ordered = columns.map(c => c.key).filter(k => chosen.has(k));
    if (!ordered.length) return toast.error('Choisissez au moins une colonne');
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/products/export`, {
        columns: ordered,
        format,
        ids: onlyFiltered && canFilter ? filteredIds : null,
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `produits_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé (${count} produits)`);
      onClose();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erreur lors de l'export"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="product-export-modal" onClick={onClose}>
      <div className="bg-[color:var(--color-surface)] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[color:var(--color-surface)]">
          <div>
            <h3 className="font-serif text-xl text-white">Exporter les produits</h3>
            <p className="text-xs text-zinc-500 mt-1">Choisissez les colonnes à inclure dans le fichier</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="export-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-6">
          <div className="flex flex-wrap gap-2 text-xs">
            <button type="button" onClick={() => setChosen(new Set(columns.map(c => c.key)))} className="px-3 py-1.5 border border-white/10 text-zinc-300 hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)]" data-testid="export-select-all">Tout cocher</button>
            <button type="button" onClick={() => setChosen(new Set())} className="px-3 py-1.5 border border-white/10 text-zinc-300 hover:border-white/30" data-testid="export-select-none">Tout décocher</button>
            <button type="button" onClick={() => setChosen(new Set(DEFAULTS))} className="px-3 py-1.5 border border-white/10 text-zinc-300 hover:border-white/30" data-testid="export-select-defaults">Sélection de base</button>
            <span className="ml-auto text-zinc-500 self-center" data-testid="export-chosen-count">{chosen.size} colonne{chosen.size > 1 ? 's' : ''}</span>
          </div>

          {Object.entries(groups).map(([group, cols]) => (
            <div key={group}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--color-primary)] mb-2">{group}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {cols.map(c => (
                  <label key={c.key} className={`flex items-center gap-2 px-3 py-2 border cursor-pointer transition-colors text-sm ${chosen.has(c.key) ? 'border-[color:rgb(var(--primary-rgb)/0.60)] bg-[color:rgb(var(--primary-rgb)/0.10)] text-white' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}>
                    <input type="checkbox" checked={chosen.has(c.key)} onChange={() => toggle(c.key)} className="accent-[color:var(--color-primary)]" data-testid={`export-col-${c.key}`} />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-white/10">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Format</p>
              <div className="flex gap-2">
                {[['csv', 'CSV', FileText], ['xlsx', 'Excel', FileSpreadsheet]].map(([v, l, Icon]) => (
                  <button key={v} type="button" onClick={() => setFormat(v)} className={`flex items-center gap-2 px-4 py-2 border text-sm ${format === v ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary)]' : 'border-white/10 text-zinc-400'}`} data-testid={`export-format-${v}`}>
                    <Icon className="w-4 h-4" /> {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Produits</p>
              <label className={`flex items-center gap-2 text-sm ${canFilter ? 'text-zinc-300 cursor-pointer' : 'text-zinc-600'}`}>
                <input type="checkbox" disabled={!canFilter} checked={onlyFiltered && canFilter} onChange={e => setOnlyFiltered(e.target.checked)} className="accent-[color:var(--color-primary)]" data-testid="export-only-filtered" />
                Seulement les {filteredIds.length} produits filtrés
              </label>
              <p className="text-xs text-zinc-500 mt-1">{count} produit{count > 1 ? 's' : ''} seront exportés</p>
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-white/10 flex justify-end gap-3 sticky bottom-0 bg-[color:var(--color-surface)]">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-white">Annuler</button>
          <button type="button" onClick={doExport} disabled={busy || !chosen.size} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="export-download-btn">
            <Download className="w-4 h-4" /> {busy ? 'Export…' : 'Télécharger'}
          </button>
        </div>
      </div>
    </div>
  );
};
